import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, TrendingUp, Users, Eye, MessageSquare } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyticsTab = () => {
  const [stats, setStats] = useState({
    page_views: {},
    language_distribution: {},
    infobox_clicks: {}
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
    // Refresh every 30 seconds
    const interval = setInterval(loadStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/analytics/stats`);
       setStats(response.data || {});
      console.log('Analytics data:', response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

     const getTotalViews = () => {
    if (!stats || !stats.total_page_views) return 0;
    return stats.total_page_views;
  };


 const getTotalClicks = () => {
    if (!stats || !stats.infobox_clicks) return 0;
    return Object.values(stats.infobox_clicks).reduce((sum, count) => sum + count, 0);
  };
   const getLanguagePercentage = (count) => {
    if (!stats || !stats.language_distribution) return 0;
    const total = Object.values(stats.language_distribution).reduce((sum, c) => sum + c, 0);
    return total > 0 ? ((count / total) * 100).toFixed(1) : 0;
  };

  if (loading) {
    return <div className="text-center p-8">Loading analytics...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Page Views</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{getTotalViews()}</div>
            <p className="text-xs text-muted-foreground">
              Feature pages visited
            </p>
          </CardContent>
        </Card>
      {/* Debates Started */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Debates Started</CardTitle>
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.debates_started || 0}</div>
          <p className="text-xs text-muted-foreground">
            Total debates initiated
          </p>
        </CardContent>
      </Card>

      {/* Debates Completed */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Debates Completed</CardTitle>
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.debates_completed || 0}</div>
          <p className="text-xs text-muted-foreground">
            Full debates finished
          </p>
        </CardContent>
      </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Infobox Clicks</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{getTotalClicks()}</div>
            <p className="text-xs text-muted-foreground">
              Landing page interactions
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Languages</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{Object.keys(stats.language_distribution).length}</div>
            <p className="text-xs text-muted-foreground">
              Different languages used
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Page Views by Feature */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart className="h-5 w-5" />
            Page Views by Feature
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(stats.top_pages || {}).map(([page, count]) => (
              <div key={page}>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium capitalize">
                    {page.replace('-', ' ')}
                  </span>
                  <span className="text-sm text-muted-foreground">{count} views</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{
                      width: `${getTotalViews() > 0 ? (count / getTotalViews()) * 100 : 0}%`
                    }}
                  />
                </div>
              </div>
            ))}
             {Object.keys(stats.top_pages || {}).length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No page views yet
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Language Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Language Distribution
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(stats.language_distribution).map(([lang, count]) => (
              <div key={lang}>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium uppercase">{lang}</span>
                  <span className="text-sm text-muted-foreground">
                    {count} views ({getLanguagePercentage(count)}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-purple-600 h-2 rounded-full transition-all"
                    style={{ width: `${getLanguagePercentage(count)}%` }}
                  />
                </div>
              </div>
            ))}
            {Object.keys(stats.language_distribution).length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No language data yet
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Infobox Clicks */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Infobox Clicks from Landing Page
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(stats.infobox_clicks).map(([element, count]) => (
              <div key={element}>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium capitalize">
                    {element.replace('-', ' ')}
                  </span>
                  <span className="text-sm text-muted-foreground">{count} clicks</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full transition-all"
                    style={{
                      width: `${getTotalClicks() > 0 ? (count / getTotalClicks()) * 100 : 0}%`
                    }}
                  />
                </div>
              </div>
            ))}
            {Object.keys(stats.infobox_clicks).length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No click data yet
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsTab;
